import React, { useContext } from "react";
import { useLocation } from "react-router-dom";
import { Box, Tooltip } from "@mui/material";
import { getTranslation } from "common";
import {
  useOrganizations,
  useSelectedOrganization,
  useTranslations,
} from "hooks";
import { OrganizationsContext } from "contexts";
import { Organization } from "model/organizationModel";
import OrganizationSelector from "pages/organizations/OrganizationSelector";

/**
 * Navbar-mounted organization switcher. It used to be a plain
 * icon-button + avatar + caption + minimal Menu; we now delegate
 * the full trigger + menu (with search) to OrganizationSelector
 * (compact variant) so the UX is consistent with the Organizations
 * page.
 */
const SelectedOrganization: React.FC = () => {
  const { pathname } = useLocation();
  const translations = useTranslations();
  const selectedOrganization = useSelectedOrganization();
  const {
    actions: { setSelectedOrganization },
  } = useContext(OrganizationsContext);

  const [organizations] = useOrganizations();

  const handleSelect = (organization: Organization) => {
    setSelectedOrganization(organization);
  };

  // The Organizations page is the *owner* of the full OrganizationSelector.
  // When the user is already there, we hide this compact duplicate to
  // avoid showing two pickers stacked in the Navbar + page header.
  // Likewise on the edit/insert routes where the form dominates the
  // viewport.
  const isOnOrganizationsPage = pathname.startsWith("/dashboard/organizations");
  const isOnEditOrInsert =
    pathname.includes("/edit") || pathname.includes("/insert");
  if (isOnOrganizationsPage || isOnEditOrInsert) {
    return null;
  }

  return (
    <Tooltip title={getTranslation(translations, "organizations.name")}>
      <Box
        sx={{
          // The compact trigger must shrink on narrow viewports so the
          // theme toggle, notification bell, and user avatar that live
          // next to it in the navbar never get pushed off-screen.
          // - minWidth: 0 lets the flex parent actually shrink us
          // - flex: "1 1 0" makes us the flex item that absorbs the
          //   leftover space (and gives it back when needed)
          // - the max caps how wide we ever get on tablets
          flex: "1 1 0",
          minWidth: 0,
          maxWidth: { xs: 160, sm: 240 },
        }}
      >
        <OrganizationSelector
          organizations={organizations}
          selectedOrganization={selectedOrganization}
          onSelect={handleSelect}
          size="compact"
        />
      </Box>
    </Tooltip>
  );
};

export default SelectedOrganization;
